import React from 'react';

export default function PrivacyScreen() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.logo}>📱</span>
          <h1 style={styles.brand}>Peekr</h1>
        </div>

        <h2 style={styles.title}>Privacy Policy</h2>
        <p style={styles.updated}>Last updated: February 2026</p>

        <Section title="Overview">
          Peekr is a real-time sharing app that lets hosts broadcast photos, videos, and documents
          to viewers via a browser — no viewer account required. We are committed to protecting
          your privacy and being transparent about how we handle your data.
        </Section>

        <Section title="Information We Collect">
          <b style={styles.b}>Content you share:</b> Photos, videos, and documents you share during
          a session are uploaded to Cloudinary (our media storage provider) for delivery
          to viewers. Files are retained by Cloudinary and subject to their retention policies.
          Peekr does not currently implement automatic deletion of files from Cloudinary after a session ends.
          <br /><br />
          <b style={styles.b}>Session data:</b> We store temporary room codes and viewer counts in
          memory on our server only for the duration of an active session. This data is deleted
          immediately when the session ends.
          <br /><br />
          <b style={styles.b}>Purchase data:</b> Subscription purchases are processed by Apple
          (App Store) and managed by RevenueCat. Peekr does not store payment details. RevenueCat
          stores anonymised purchase records to manage your subscription status.
          <br /><br />
          <b style={styles.b}>Device identifiers:</b> RevenueCat uses an anonymous device identifier
          to track subscription entitlements. No personally identifiable information is linked to this.
        </Section>

        <Section title="Information We Do Not Collect">
          We do not collect your name, email address, or any account information. Viewers join
          sessions without creating an account. We do not track your location. We do not use
          advertising or analytics SDKs. We do not sell or share your data with third parties
          for marketing purposes.
        </Section>

        <Section title="Third-Party Services">
          Peekr uses the following third-party services:
          <br /><br />
          <b style={styles.b}>Cloudinary</b> — media storage and delivery. Files uploaded during
          sessions are stored temporarily. Cloudinary's privacy policy: cloudinary.com/privacy<br /><br />
          <b style={styles.b}>RevenueCat</b> — subscription management. Privacy policy:
          revenuecat.com/privacy<br /><br />
          <b style={styles.b}>Railway</b> — server infrastructure. Session data passes through
          Railway's servers and is not persisted. Privacy policy: railway.app/legal/privacy
        </Section>

        <Section title="Data Retention">
          Session content (photos, videos, documents) uploaded during a session is stored
          by Cloudinary and subject to their retention policies. Peekr does not currently
          implement automatic deletion of media files from Cloudinary. Session room data is held in server memory only and deleted the moment
          a session closes. Subscription status is retained by RevenueCat as long as you have
          an active or lapsed subscription.
        </Section>

        <Section title="Children's Privacy">
          Peekr is not directed at children under 13. We do not knowingly collect personal
          information from children. If you believe a child has shared personal information
          through Peekr, contact us and we will delete it promptly.
        </Section>

        <Section title="Your Rights">
          Since Peekr does not collect personal accounts, there is no user data to request,
          modify, or delete from our systems. If you have purchased a subscription and wish to
          manage or delete your RevenueCat data, you can do so through your Apple ID settings
          or by contacting us.
        </Section>

        <Section title="Changes to This Policy">
          We may update this policy as Peekr evolves. Material changes will be noted on this
          page with an updated date. Continued use of Peekr after changes constitutes acceptance.
        </Section>

        <Section title="Contact">
          Questions about this policy? Contact us at:<br /><br />
          <b style={styles.b}>Ebenova Solutions</b><br />
          privacy@getpeekr.com
        </Section>

        <div style={styles.footer}>
          <a href="/" style={styles.link}>← Back to Peekr</a>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <p style={styles.sectionBody}>{children}</p>
    </div>
  );
}

const styles = {
  page: { backgroundColor: '#050C1A', minHeight: '100vh', color: '#FFFFFF', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  container: { maxWidth: 680, margin: '0 auto', padding: '48px 24px' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40 },
  logo: { fontSize: 32 },
  brand: { fontSize: 28, fontWeight: 800, color: '#FFFFFF', letterSpacing: 2, margin: 0 },
  title: { fontSize: 32, fontWeight: 700, color: '#FFFFFF', marginBottom: 8 },
  updated: { fontSize: 13, color: '#3A5570', marginBottom: 40 },
  section: { marginBottom: 36, borderLeft: '2px solid #122038', paddingLeft: 20 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#00D4FF', marginBottom: 10, letterSpacing: 1 },
  sectionBody: { fontSize: 14, color: '#6A85A0', lineHeight: 1.8, margin: 0 },
  b: { color: '#FFFFFF', fontWeight: 600 },
  footer: { marginTop: 60, paddingTop: 24, borderTop: '1px solid #122038' },
  link: { color: '#00D4FF', textDecoration: 'none', fontSize: 14 },
};
